import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { Check, X, Loader2, Play, Square } from 'lucide-react';
import { toast } from 'sonner';
import { reencode } from '@/utils/videoReencode';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VideoEditorModalProps {
  isOpen: boolean;
  videoSrc: string;
  onSave: (blob: Blob, previewUrl: string) => void;
  onCancel: () => void;
  maxDuration?: number;    // seconds — when set, trim window cannot exceed this length
  forceReencode?: boolean; // when true, always re-encode even if handles are unchanged
}

const MIN_TRIM_DURATION = 1; // seconds

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const VideoEditorModal: React.FC<VideoEditorModalProps> = ({
  isOpen,
  videoSrc,
  onSave,
  onCancel,
  maxDuration,
  forceReencode,
}) => {
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const trimVideoRef  = useRef<HTMLVideoElement>(null);
  const timelineRef   = useRef<HTMLDivElement>(null);
  const previewRafRef = useRef<number>(0);
  const draggingHandle = useRef<'start' | 'end' | null>(null);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setDuration(0);
      setTrimStart(0);
      setTrimEnd(0);
      setIsPreviewing(false);
      setIsProcessing(false);
      setProgress(0);
    }
  }, [isOpen, videoSrc]);

  // ---------------------------------------------------------------------------
  // Metadata
  // ---------------------------------------------------------------------------

  const handleMetadata = useCallback(() => {
    const v = trimVideoRef.current;
    if (!v) return;
    const d = isFinite(v.duration) ? v.duration : 0;
    setDuration(d);
    setTrimStart(0);
    setTrimEnd(maxDuration ? Math.min(maxDuration, d) : d);
    // Force first frame to paint on iOS Safari webapp mode — without an explicit
    // seek the video element stays black until the user interacts with it.
    v.currentTime = 0.01;
  }, [maxDuration]);

  // ---------------------------------------------------------------------------
  // Preview
  // ---------------------------------------------------------------------------

  const handlePreview = useCallback(() => {
    const v = trimVideoRef.current;
    if (!v || duration === 0) return;

    if (isPreviewing) {
      v.pause();
      cancelAnimationFrame(previewRafRef.current);
      setIsPreviewing(false);
      return;
    }

    v.currentTime = trimStart;
    v.play().then(() => {
      setIsPreviewing(true);
      const check = () => {
        if (!trimVideoRef.current || trimVideoRef.current.currentTime >= trimEnd) {
          trimVideoRef.current?.pause();
          setIsPreviewing(false);
          return;
        }
        previewRafRef.current = requestAnimationFrame(check);
      };
      previewRafRef.current = requestAnimationFrame(check);
    }).catch(() => setIsPreviewing(false));
  }, [trimStart, trimEnd, duration, isPreviewing]);

  // ---------------------------------------------------------------------------
  // Timeline drag
  // ---------------------------------------------------------------------------

  const getTimeFromPointerX = useCallback((clientX: number): number => {
    const el = timelineRef.current;
    if (!el || duration === 0) return 0;
    const rect = el.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return frac * duration;
  }, [duration]);

  const handlePointerDown = useCallback((e: React.PointerEvent, handle: 'start' | 'end') => {
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingHandle.current = handle;
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggingHandle.current) return;
    const t = getTimeFromPointerX(e.clientX);
    if (draggingHandle.current === 'start') {
      const newStart = Math.max(0, Math.min(t, trimEnd - MIN_TRIM_DURATION));
      setTrimStart(newStart);
      if (maxDuration && trimEnd - newStart > maxDuration) {
        setTrimEnd(newStart + maxDuration);
      }
    } else {
      const maxEnd = maxDuration
        ? Math.min(duration, trimStart + maxDuration)
        : duration;
      setTrimEnd(Math.min(maxEnd, Math.max(t, trimStart + MIN_TRIM_DURATION)));
    }
  }, [getTimeFromPointerX, trimStart, trimEnd, duration, maxDuration]);

  const handlePointerUp = useCallback(() => {
    draggingHandle.current = null;
  }, []);

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    if (isProcessing) return;

    // If no trim applied and re-encoding isn't forced, pass original through
    const noTrim = !forceReencode && trimStart === 0 && (duration === 0 || trimEnd >= duration - 0.05);
    if (noTrim) {
      // Fetch the blob from the blob URL directly to avoid re-encoding
      try {
        const res = await fetch(videoSrc);
        const blob = await res.blob();
        onSave(blob, videoSrc);
      } catch {
        onSave(new Blob(), videoSrc);
      }
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    try {
      const blob = await reencode(videoSrc, trimStart, trimEnd, setProgress, trimVideoRef.current ?? undefined);
      onSave(blob, URL.createObjectURL(blob));
    } catch (err) {
      console.error('[VideoEditorModal] Trim failed:', err);
      toast.error('Failed to trim video. Please try again.');
      setIsProcessing(false);
      setProgress(0);
    }
  }, [isProcessing, videoSrc, trimStart, trimEnd, duration, onSave, forceReencode]);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const startFrac = duration > 0 ? trimStart / duration : 0;
  const endFrac   = duration > 0 ? trimEnd   / duration : 1;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && !isProcessing && onCancel()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-[101] flex flex-col bg-black outline-none"
          style={{ height: '100dvh' }}
        >
          {/* Progress bar */}
          {isProcessing && (
            <div className="flex-shrink-0 h-0.5 bg-white/10" style={{ marginTop: 'env(safe-area-inset-top)' }}>
              <div
                className="h-full bg-[#cfef00] transition-all duration-100"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
          )}

          {isProcessing && (
            <div className="flex-shrink-0 py-2 text-center">
              <p className="text-xs text-white/50">
                Processing… {Math.round(progress * 100)}%
              </p>
            </div>
          )}

          {/* Video preview */}
          <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden">
            <video
              ref={trimVideoRef}
              src={videoSrc}
              className="max-w-full max-h-full object-contain"
              muted
              playsInline
              preload="auto"
              onLoadedMetadata={handleMetadata}
            />
          </div>

          {/* Trim controls */}
          <div
            className="flex-shrink-0 px-4 pt-5 space-y-4 bg-black"
            style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
          >
            {/* Time labels */}
            <div className="flex justify-between text-xs text-white/50">
              <span>{formatTime(trimStart)}</span>
              <span className={cn(
                'font-medium',
                maxDuration && (trimEnd - trimStart) >= maxDuration - 0.1
                  ? 'text-[#cfef00]'
                  : 'text-white/70',
              )}>
                {(trimEnd - trimStart).toFixed(1)}s selected
              </span>
              <span>{formatTime(trimEnd)}</span>
            </div>

            {/* Timeline */}
            <div
              ref={timelineRef}
              className="relative h-10 bg-white/10 rounded-full select-none"
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              {/* Selected range highlight */}
              <div
                className="absolute top-0 bottom-0 bg-[#cfef00]/25 border-y-2 border-[#cfef00]/60"
                style={{ left: `${startFrac * 100}%`, right: `${(1 - endFrac) * 100}%` }}
              />

              {/* Start handle */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-5 h-9 bg-[#cfef00] rounded-full cursor-grab active:cursor-grabbing touch-none shadow-lg flex items-center justify-center"
                style={{ left: `calc(${startFrac * 100}% - 10px)` }}
                onPointerDown={(e) => handlePointerDown(e, 'start')}
              >
                <div className="w-0.5 h-4 bg-black/40 rounded-full" />
              </div>

              {/* End handle */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-5 h-9 bg-[#cfef00] rounded-full cursor-grab active:cursor-grabbing touch-none shadow-lg flex items-center justify-center"
                style={{ left: `calc(${endFrac * 100}% - 10px)` }}
                onPointerDown={(e) => handlePointerDown(e, 'end')}
              >
                <div className="w-0.5 h-4 bg-black/40 rounded-full" />
              </div>
            </div>

            {/* Preview button */}
            <button
              onClick={handlePreview}
              disabled={duration === 0 || isProcessing}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-all disabled:opacity-40',
                isPreviewing
                  ? 'border-[#cfef00]/50 text-[#cfef00] bg-[#cfef00]/10'
                  : 'border-white/20 text-white/80 hover:border-white/40 hover:text-white',
              )}
            >
              {isPreviewing ? (
                <><Square className="h-4 w-4 fill-current" /> Stop Preview</>
              ) : (
                <><Play className="h-4 w-4 fill-current" /> Preview Trim</>
              )}
            </button>

            {/* Close / Apply row */}
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                disabled={isProcessing}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-white/20 text-white/80 text-sm font-medium transition-all disabled:opacity-30 hover:border-white/40 hover:text-white"
              >
                <X className="h-4 w-4" /> Close
              </button>
              <button
                onClick={handleSave}
                disabled={isProcessing}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#cfef00] text-black text-sm font-semibold transition-all disabled:opacity-30"
              >
                {isProcessing
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Check className="h-4 w-4" />
                }
                Apply
              </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default VideoEditorModal;
